import { Card, CardContent } from '@/components/ui/card';
import { Target, Eye, Award, Users, TrendingUp, MapPin } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 to-white py-20 px-4" data-testid="about-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">About Frogleap Technologies</h1>
          <p className="text-xl text-gray-600">
            Revolutionizing the travel industry with innovative SaaS solutions that empower travel partners
            to scale, automate, and deliver exceptional experiences.
          </p>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-16 bg-white" data-testid="company-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Story</h2>
              <p className="text-gray-600 mb-4">
                Founded in the vibrant city of Pune, Maharashtra, Frogleap Technologies was born from a vision
                to transform how travel partners manage their operations in an increasingly digital world.
              </p>
              <p className="text-gray-600 mb-4">
                We recognized that travel agencies and corporate travel managers were struggling with fragmented
                systems, manual processes, and the inability to scale efficiently. Our founders, with decades of
                combined experience in travel technology and enterprise software, set out to build a comprehensive
                platform that would address these challenges head-on.
              </p>
              <p className="text-gray-600">
                Today, Frogleap Technologies stands as a leading SaaS platform provider, enabling travel partners
                to automate operations, reduce manual work by up to 80%, and deliver exceptional experiences through
                our white-labeled mobile apps and web platforms.
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-100 to-red-50 p-8 rounded-lg">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Headquartered in Pune</h3>
                    <p className="text-gray-600 text-sm">Based in the thriving tech hub of Maharashtra, India</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <Users className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Expert Team</h3>
                    <p className="text-gray-600 text-sm">Seasoned professionals in travel tech and enterprise solutions</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <TrendingUp className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Rapid Growth</h3>
                    <p className="text-gray-600 text-sm">Serving travel partners across multiple regions and industries</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-gray-50" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Target className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-600">
                  To empower travel partners worldwide with innovative, scalable SaaS solutions that automate
                  operations, reduce manual work, and enable them to focus on what matters most—delivering
                  exceptional travel experiences to their customers.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <Eye className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-600">
                  To become the world's most trusted and comprehensive SaaS platform for the travel industry,
                  setting new standards in automation, efficiency, and digital transformation for travel agencies,
                  corporate travel managers, and transportation service providers.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide every decision we make and every solution we build.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="value-innovation">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Innovation First</h3>
              <p className="text-gray-600">
                We constantly push boundaries to develop cutting-edge solutions that stay ahead of industry trends
                and meet evolving customer needs.
              </p>
            </div>

            <div className="text-center" data-testid="value-customer">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Customer Success</h3>
              <p className="text-gray-600">
                Your success is our success. We're committed to providing exceptional support and ensuring every
                partner achieves their business goals.
              </p>
            </div>

            <div className="text-center" data-testid="value-excellence">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Operational Excellence</h3>
              <p className="text-gray-600">
                We believe in delivering robust, reliable, and scalable solutions that our partners can depend on
                24/7/365.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50" data-testid="why-choose-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Travel Partners Choose Frogleap</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              'Comprehensive end-to-end SaaS platform for all travel operations',
              'White-labeled mobile apps (iOS & Android) for brand consistency',
              'Powerful admin panel with personalized dashboards',
              'Automated booking, invoicing, and vendor management',
              'Enterprise Travel Solutions (ETS) for corporate clients',
              'Dedicated driver app with real-time updates',
              '99.9% uptime and error-free dispatching',
              '24/7 technical support and ongoing platform updates',
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3" data-testid={`reason-${index}`}>
                <div className="flex-shrink-0 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center mt-0.5">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-gray-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-700 text-white" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Partner with Frogleap Technologies?</h2>
          <p className="text-xl mb-8 text-red-100">
            Let's discuss how our platform can transform your travel operations.
          </p>
          <a href="/contact">
            <button className="bg-white text-red-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors" data-testid="about-contact-btn">
              Get in Touch
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;